import { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { demo, StatusBadge, SyntheticTag, money, STATE_ORDER, STATUS_META } from "./c1lib";
import { Section } from "./PublicLayout";
import { toast } from "sonner";
import { BadgeCheck, XCircle, Fingerprint, FileCheck2, Landmark, Clock, ChevronLeft, ShieldCheck, ShieldAlert, FlaskConical, Share2, ShieldOff } from "lucide-react";

export default function RecordDetail({ share = false }) {
  const { rid } = useParams();
  const [d, setD] = useState(null);
  const [err, setErr] = useState("");
  const load = useCallback(() => { demo.record(rid).then(setD).catch(() => setErr("Record not found")); }, [rid]);
  useEffect(() => { load(); }, [load]);

  if (err) return <Section title="Not found"><p className="text-slate-500">{err}</p></Section>;
  if (!d) return <Section><p className="text-slate-400 font-mono text-sm">Loading…</p></Section>;

  const r = d.record;
  const certified = r.status === "CERTIFIED";
  const withheld = r.status === "CERTIFICATION_WITHHELD";
  const fin = r.financial_record;

  // Public-copy rewrites — display-only. Stored data (and integrity hash) unchanged.
  // Replaces the internal governance reference "PR-O003" with plain language in public prose.
  const publicize = (s) => {
    if (!s) return s;
    return s
      // "Certified against the written PR-O003 minimum evidence standard using ..." → drop "the written"
      .replace(/against the written PR-O003 minimum evidence standard using/gi,
               "against Pramaana's minimum evidence standard using")
      // Mitigation form keeps "written"
      .replace(/against the written PR-O003 minimum evidence standard/gi,
               "against Pramaana's written minimum evidence standard")
      .replace(/written PR-O003 minimum evidence standard/gi,
               "Pramaana's written minimum evidence standard")
      // Audit-trail form: "Trust chain complete against PR-O003; record certified."
      .replace(/Trust chain complete against PR-O003;\s*record certified\./gi,
               "Trust chain met Pramaana's minimum evidence standard; record certified.")
      .replace(/complete against PR-O003/gi,
               "met Pramaana's minimum evidence standard")
      .replace(/against PR-O003;/gi,
               "against Pramaana's minimum evidence standard;")
      .replace(/PR-O003 minimum evidence standard/gi,
               "Pramaana's minimum evidence standard")
      // Any remaining bare "PR-O003" in public prose
      .replace(/\bPR-O003\b/g, "Pramaana's minimum evidence standard");
  };

  const shareRecord = async () => {
    const url = window.location.href;
    const title = `Pramaana Verified Record | ${r.seva_snapshot?.name || "Verified Record"}`;
    try {
      if (navigator.share) await navigator.share({ title, text: title, url });
      else { await navigator.clipboard.writeText(url); toast.success("Link copied to clipboard"); }
    } catch { /* user cancelled share */ }
  };

  return (
    <Section>
      {share
        ? <button onClick={shareRecord} data-testid="share-record" className="float-right inline-flex items-center gap-1.5 text-sm text-slate-100 bg-slate-900 hover:bg-slate-800 rounded-sm px-3 py-1.5 mb-6 transition-colors"><Share2 size={14} /> Share</button>
        : <Link to="/records" className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-900 mb-6" data-testid="back-records"><ChevronLeft size={15} /> All records</Link>}

      <div className="bg-white border border-slate-200 rounded-sm overflow-hidden" data-testid="verified-record">
        {/* certificate header */}
        <div className="border-b border-slate-200 p-8" style={{ background: certified ? "#F7FCF9" : "#F8FAFC" }}>
          <div className="flex items-start justify-between flex-wrap gap-4">
            <div>
              <div className="flex items-center gap-3">
                {r.is_live
                  ? <span data-testid="live-tag" className="inline-flex items-center gap-1 border border-emerald-300 bg-emerald-50 text-emerald-800 px-2 py-0.5 rounded-sm font-mono text-[9px] uppercase tracking-[0.18em]">Live · Certified record</span>
                  : <SyntheticTag />}
                {r.case_label && <span className="font-mono text-[10px] uppercase tracking-wider text-slate-400">{r.case_label}</span>}
              </div>
              <h1 className="font-serif text-4xl font-semibold tracking-tight text-slate-950 mt-3">Pramaana Verified Record</h1>
              <div className="font-mono text-sm text-slate-600 mt-1.5" data-testid="record-id">{r.record_id}</div>
            </div>
            <div className="text-right">
              {certified ? <BadgeCheck size={30} className="text-emerald-600 ml-auto" /> : withheld ? <XCircle size={30} className="text-slate-500 ml-auto" /> : <Clock size={30} className="text-amber-500 ml-auto" />}
              <div className="mt-2" data-testid="record-status"><StatusBadge status={r.status} /></div>
            </div>
          </div>
          {withheld && d.validation?.reason && (
            <div className="mt-5 border border-slate-300 bg-white rounded-sm p-4 text-sm text-slate-700" data-testid="withheld-reason">
              <span className="font-mono text-[10px] uppercase tracking-wider text-slate-600">Why certification was withheld</span>
              <p className="mt-1 text-slate-700">{publicize(d.validation.reason)}</p>
            </div>
          )}
          {certified && d.validation?.reason && (
            <div className="mt-5 border border-emerald-200 bg-white rounded-sm p-4 text-sm text-slate-700" data-testid="certified-reason">
              <span className="font-mono text-[10px] uppercase tracking-wider text-emerald-700">Why certification was granted</span>
              <p className="mt-1 text-slate-700">{publicize(d.validation.reason)}</p>
            </div>
          )}
        </div>

        <div className="p-8 space-y-8">
          <Stepper status={r.status} />

          <Grid>
            <Field label="Temple" value={d.temple?.name} sub={`${d.temple?.location || ""}`} />
            <Field label="Temple Verification" value={d.temple?.verification_status?.replace("_", " — ")} sub={r.temple_verification_id} icon={Landmark} />
            <Field label="Seva" value={r.seva_snapshot?.name} sub={r.seva_snapshot?.fulfilment_type} />
            <Field label="Requested / Fulfilment Date" value={r.requested_date} sub={r.fulfilment?.fulfilment_date ? `Fulfilled ${r.fulfilment.fulfilment_date}` : "Not fulfilled"} />
          </Grid>

          {/* Sankalpa */}
          <Block title="Sankalpa" testid="record-sankalpa">
            <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-2 text-sm">
              {Object.entries(r.sankalpa || {}).map(([k, v]) => v ? (
                <div key={k} className="flex justify-between gap-4 border-b border-slate-100 py-1">
                  <dt className="text-slate-500 capitalize">{k.replace(/_/g, " ")}</dt>
                  <dd className="text-slate-800 text-right">{v}</dd>
                </div>
              ) : null)}
            </dl>
          </Block>

          {/* Financial */}
          <Block title="Transparent Financial Record" right={fin?.model_label ? <span className="font-mono text-[9px] uppercase tracking-wider text-amber-700 bg-amber-50 border border-amber-200 px-2 py-0.5 rounded-sm">{fin.model_label}</span> : null} testid="record-financial">
            <table className="w-full text-sm">
              <tbody>
                {fin?.line_items?.map((li) => (
                  <tr key={li.key} className="border-b border-slate-100">
                    <td className="py-2 text-slate-700">{li.label}<div className="text-[11px] text-slate-500">{li.note} · belongs to {li.belongs_to}</div></td>
                    <td className="py-2 text-right font-mono text-slate-800 align-top">{money(li.amount, fin.currency)}</td>
                  </tr>
                ))}
                <tr>
                  <td className="py-3 font-medium text-slate-900">Total{fin?.payment_method ? ` (paid via ${fin.payment_method})` : " (synthetic payment)"}</td>
                  <td className="py-3 text-right font-mono font-semibold text-slate-950">{money(fin?.total, fin?.currency)}</td>
                </tr>
              </tbody>
            </table>
            {fin?.financial_evidence && (
              <div className="mt-2 text-[11px] text-slate-600" data-testid="financial-evidence">
                <span className="font-mono uppercase tracking-wider text-slate-500">Financial evidence — Tier {fin.financial_evidence.tier}:</span> {fin.financial_evidence.basis?.join(" · ")}
              </div>
            )}
            <p className="text-[11px] text-slate-500 mt-2">{r.payment?.note}</p>
          </Block>

          {/* Evidence */}
          <Block title="Evidence" subtitle="Submitted ≠ accepted. Each item carries an integrity hash; acceptance is decided during validation." testid="record-evidence">
            {d.evidence.length === 0 ? <p className="text-sm text-slate-400">No evidence submitted.</p> : (
              <div className="space-y-3">
                {d.evidence.map((e) => (
                  <div key={e.id} className="border border-slate-200 rounded-sm p-4 bg-[#F8FAFC]" data-testid={`evidence-${e.type}`}>
                    <div className="flex items-center gap-2 flex-wrap">
                      <FileCheck2 size={15} className="text-slate-500" />
                      <span className="font-mono text-xs text-slate-700 bg-white border border-slate-200 px-1.5 rounded-sm">{e.type}</span>
                      {e.evidence_class && <span className="text-[10px] text-slate-500">{e.evidence_class}</span>}
                      <EvidenceState state={e.validation_state} />
                      <span className="font-mono text-[10px] text-slate-500">{e.hash_type === "FILE" ? `${e.filename} · ${e.size}B` : "metadata"}</span>
                      <span className="font-mono text-[10px] text-slate-500 ml-auto">by {e.submitted_by} · {e.source}</span>
                    </div>
                    {e.public_safe && e.storage_ref && String(e.storage_ref).startsWith("http") && String(e.mime || "").startsWith("image") && (
                      <a href={e.storage_ref} target="_blank" rel="noreferrer" className="block mt-3">
                        <img src={e.storage_ref} alt={e.type} loading="lazy"
                          className="max-h-56 rounded-sm border border-slate-200 object-contain" data-testid={`evidence-img-${e.type}`} />
                      </a>
                    )}
                    {e.public_safe === false && (
                      <div className="mt-3 flex items-start gap-2 border border-slate-200 bg-white rounded-sm p-3 text-[11px] text-slate-600" data-testid={`evidence-redacted-${e.type}`}>
                        <ShieldOff size={13} className="text-slate-400 mt-0.5 shrink-0" />
                        <span>Public-safe representation. The original document may contain personal details, so only its verified summary and integrity hash are shown here. The original is preserved unchanged for provenance and integrity.</span>
                      </div>
                    )}
                    {e.note && <p className="text-xs text-slate-600 mt-2">{e.note}</p>}
                    <div className="mt-2 text-[11px] font-mono text-slate-600 break-all">
                      <span className="uppercase tracking-wider text-slate-500">{e.hash_label}</span><br />sha256: {e.sha256}
                    </div>
                  </div>
                ))}
              </div>
            )}
            <p className="text-xs text-slate-500 mt-4 leading-relaxed border-t border-slate-100 pt-3" data-testid="tradition-principle">
              Pramaana respects each temple's traditions. Where the temple permits it, a Verified Record may include photographs, audio or video.
              Where recording is restricted or inappropriate, Pramaana uses the strongest temple-authorised evidence available.
              A temple should never be required to alter its traditions merely to satisfy a technology platform.
            </p>
          </Block>

          {/* Validation */}
          <Block title="Validation" testid="record-validation">
            {d.validation ? (
              <div className={`border rounded-sm p-4 ${d.validation.decision === "VALIDATED" ? "border-emerald-200 bg-emerald-50" : "border-slate-300 bg-slate-50"}`}>
                <div className="flex items-center gap-2 flex-wrap">
                  <ShieldCheck size={16} className={d.validation.decision === "VALIDATED" ? "text-emerald-600" : "text-slate-500"} />
                  <span className="font-mono text-xs uppercase tracking-wider text-slate-700">{d.validation.validator}{d.validation.validator_role ? ` · ${d.validation.validator_role}` : ""} — {d.validation.decision}</span>
                </div>
                <p className="text-sm text-slate-700 mt-2">{publicize(d.validation.reason)}</p>
                {d.validation.conflict_disclosure && (
                  <div className="mt-3 border border-amber-200 bg-amber-50 rounded-sm p-3" data-testid="conflict-disclosure">
                    <span className="font-mono text-[10px] uppercase tracking-wider text-amber-800">Conflict of interest — disclosed</span>
                    <p className="text-xs text-slate-700 mt-1">{d.validation.conflict_disclosure}</p>
                    {d.validation.mitigation && <p className="text-xs text-slate-700 mt-1"><span className="text-slate-500">Mitigation: </span>{publicize(d.validation.mitigation)}</p>}
                  </div>
                )}
                {d.validation.llm_role && (
                  <p className="text-[11px] text-slate-500 mt-2" data-testid="llm-role"><span className="font-mono uppercase tracking-wider">LLM role:</span> {d.validation.llm_role}</p>
                )}
                {certified && (
                  <div className="mt-3 border-t border-emerald-100 pt-3 text-[11px] text-slate-500 leading-relaxed" data-testid="certification-standard-ref">
                    <div><span className="font-mono uppercase tracking-wider text-slate-500">Certification standard:</span> Pramaana Minimum Evidence Standard</div>
                    <div><span className="font-mono uppercase tracking-wider text-slate-400">Internal governance reference:</span> PR-O003</div>
                  </div>
                )}
              </div>
            ) : <p className="text-sm text-slate-400">Not yet validated.</p>}
          </Block>

          {/* Integrity */}
          <Block title="Record Integrity" subtitle="SHA-256 proves the stored record is internally consistent and tamper-evident. It does NOT prove a religious act occurred." testid="record-integrity">
            <div className="border border-slate-300 rounded-sm p-4 bg-slate-950 text-slate-100">
              <div className="flex items-center gap-2 mb-2">
                <Fingerprint size={16} className="text-emerald-400" />
                <span className="font-mono text-xs uppercase tracking-wider">Record Integrity: VERIFIED</span>
                <span className="font-mono text-[10px] text-slate-400 ml-auto">v{r.record_version}</span>
              </div>
              <div className="font-mono text-[11px] break-all text-emerald-300" data-testid="integrity-hash">SHA-256: {d.integrity.record_integrity_hash}</div>
            </div>
            {d.versions?.length > 0 && (
              <div className="mt-3 text-[11px] font-mono text-slate-500 space-y-1" data-testid="version-history">
                {d.versions.map((v) => (
                  <div key={v.id}>v{v.version} · {v.certification_status} · {v.integrity_hash.slice(0, 24)}…{v.references_version ? ` (→ v${v.references_version})` : ""}</div>
                ))}
              </div>
            )}
          </Block>

          {!share && <TamperPanel rid={r.record_id} originalHash={d.integrity.record_integrity_hash} />}

          {/* Audit trail */}
          <Block title="Audit Trail" subtitle="Evidentiary record of every state transition — actor, role, previous → new state, and reason." testid="record-audit">
            <div className="relative border-l-2 border-slate-200 ml-1">
              {d.events.map((ev) => (
                <div key={ev.id} className="relative pl-6 pb-6" data-testid={`event-${ev.action}`}>
                  <span className="absolute -left-[7px] top-1 w-3 h-3 bg-slate-900 rounded-full ring-2 ring-white" />
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-mono text-[11px] uppercase tracking-wider text-slate-800 border border-slate-300 bg-slate-50 px-1.5 py-0.5 rounded-sm">{ev.action.replace(/_/g, " ")}</span>
                    {ev.prev_state && <span className="font-mono text-[11px] text-slate-600">{ev.prev_state} → <span className="text-slate-900 font-medium">{ev.new_state}</span></span>}
                    <span className="font-mono text-[11px] text-slate-600 ml-auto"><span className="text-slate-800 font-medium">{ev.actor}</span> · {new Date(ev.at).toLocaleString()}</span>
                  </div>
                  {ev.reason && <p className="text-sm text-slate-700 mt-1.5 leading-relaxed">{publicize(ev.reason)}</p>}
                </div>
              ))}
            </div>
          </Block>
        </div>
      </div>
    </Section>
  );
}

const TAMPER_SCENARIOS = [
  { key: "sankalpa", label: "Alter Sankalpa intention" },
  { key: "amount", label: "Inflate temple amount" },
  { key: "evidence", label: "Swap an evidence hash" },
];

function TamperPanel({ rid, originalHash }) {
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState("");

  const run = async (scenario) => {
    setBusy(scenario);
    try { setRes(await demo.tamperPreview(rid, { scenario })); }
    catch { setRes(null); }
    finally { setBusy(""); }
  };

  return (
    <Block title="Integrity Verification — Tamper Test"
      subtitle="See how Pramaana detects a changed record. This simulation changes a copy of the record and recomputes its SHA-256 — the stored record is never modified. It demonstrates tamper-evidence, not that a religious act occurred."
      testid="tamper-panel">
      <div className="flex flex-wrap gap-2 mb-3">
        {TAMPER_SCENARIOS.map((s) => (
          <button key={s.key} onClick={() => run(s.key)} disabled={!!busy}
            data-testid={`tamper-${s.key}`}
            className="inline-flex items-center gap-1.5 border border-slate-300 bg-white rounded-sm px-3 py-1.5 text-xs text-slate-700 hover:border-slate-500 transition-colors disabled:opacity-50">
            <FlaskConical size={13} /> {busy === s.key ? "Recomputing…" : s.label}
          </button>
        ))}
      </div>
      {res && (
        <div className="border border-slate-300 rounded-sm overflow-hidden" data-testid="tamper-result">
          <div className={`flex items-center gap-2 px-4 py-2.5 ${res.tamper_detected ? "bg-rose-50 border-b border-rose-200" : "bg-emerald-50 border-b border-emerald-200"}`}>
            <ShieldAlert size={16} className={res.tamper_detected ? "text-rose-600" : "text-emerald-600"} />
            <span className="font-mono text-xs uppercase tracking-wider text-slate-900" data-testid="tamper-verdict">
              {res.tamper_detected ? "Tamper detected" : "No change detected"}
            </span>
            <span className="font-mono text-[9px] uppercase tracking-wider text-slate-500 ml-auto">the stored record remains unchanged</span>
          </div>
          <div className="p-4 space-y-2 bg-white">
            {res.tamper_detected && <div className="text-xs text-rose-700 font-medium">Stored and recomputed hashes do not match.</div>}
            <div className="text-xs text-slate-600">{res.description}</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-[11px] font-mono">
              <div className="border border-slate-200 rounded-sm p-2"><span className="text-slate-500 uppercase">Before</span><div className="text-slate-800 break-words">{res.before}</div></div>
              <div className="border border-slate-200 rounded-sm p-2"><span className="text-slate-500 uppercase">Simulated change</span><div className="text-slate-800 break-words">{res.after}</div></div>
            </div>
            <div className="font-mono text-[11px] break-all text-slate-600">original: {res.original_hash}</div>
            <div className={`font-mono text-[11px] break-all ${res.tamper_detected ? "text-rose-600" : "text-slate-600"}`}>recomputed: {res.recomputed_hash}</div>
          </div>
        </div>
      )}
    </Block>
  );
}

function Stepper({ status }) {
  const withheld = status === "CERTIFICATION_WITHHELD";
  const curIdx = STATE_ORDER.indexOf(status);
  return (
    <div className="flex items-center flex-wrap gap-y-2" data-testid="status-stepper">
      {STATE_ORDER.map((s, i) => {
        const done = curIdx >= i && curIdx !== -1;
        const isWithholdPoint = withheld && s === "VALIDATED";
        return (
          <div key={s} className="flex items-center">
            <div className={`flex items-center gap-1.5 px-2 py-1 rounded-sm border font-mono text-[9px] uppercase tracking-wider ${
              isWithholdPoint ? "border-slate-400 bg-slate-100 text-slate-600"
                : done ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                : "border-slate-200 bg-white text-slate-400"}`}>
              {STATUS_META[s]?.label || s}
            </div>
            {i < STATE_ORDER.length - 1 && <span className={`w-4 h-px ${done ? "bg-emerald-300" : "bg-slate-200"}`} />}
          </div>
        );
      })}
      {withheld && <span className="ml-2 font-mono text-[9px] uppercase tracking-wider px-2 py-1 rounded-sm border border-slate-400 bg-slate-100 text-slate-700">Certification Withheld</span>}
    </div>
  );
}

function EvidenceState({ state }) {
  const map = { ACCEPTED: "border-emerald-200 bg-emerald-50 text-emerald-700", REJECTED: "border-slate-300 bg-slate-100 text-slate-600", SUBMITTED: "border-amber-200 bg-amber-50 text-amber-700" };
  return <span className={`font-mono text-[9px] uppercase tracking-wider border px-1.5 rounded-sm ${map[state] || map.SUBMITTED}`}>{state}</span>;
}

function Grid({ children }) { return <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">{children}</div>; }
function Field({ label, value, sub, icon: Icon }) {
  return (
    <div className="border border-slate-200 rounded-sm p-4 bg-white">
      <div className="flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-wider text-slate-400 mb-1.5">{Icon && <Icon size={12} />}{label}</div>
      <div className="text-sm font-medium text-slate-900">{value || "—"}</div>
      {sub && <div className="text-[11px] text-slate-500 mt-0.5">{sub}</div>}
    </div>
  );
}
function Block({ title, subtitle, right, children, testid }) {
  return (
    <div data-testid={testid}>
      <div className="flex items-center justify-between mb-3">
        <div>
          <h3 className="font-mono text-[11px] uppercase tracking-[0.15em] text-slate-500">{title}</h3>
          {subtitle && <p className="text-xs text-slate-500 mt-1 max-w-2xl leading-relaxed">{subtitle}</p>}
        </div>
        {right}
      </div>
      {children}
    </div>
  );
}
